import { useState, useEffect } from "react";
import { Calendar, Filter, Loader2, Search, Headphones, Building, FileText, X } from "lucide-react";
import Menu from "../../components/Menu";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import api from "../../api";
import Swal from "sweetalert2";
import { pdf } from "@react-pdf/renderer";
import { ReportPDFAttendants } from "../../components/ReportPDFAttendants";
import formatDate from "../../utils/formatDate";

export default function AttendantsTransfer() {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  const formatDate2 = (date) => date.toISOString().split("T")[0]

  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_start: formatDate2(firstDay),
    date_end: formatDate2(lastDay),
    user_id: null,
    procedure_id: null,
    executing_company: null,
  });

  const [attendants, setAttendants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [attendantSearch, setAttendantSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");

  const [isAttendantSelected, setIsAttendantSelected] = useState(false);
  const [isCompanySelected, setIsCompanySelected] = useState(false);

  const [selectedAttendant, setSelectedAttendant] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [procedures, setProcedures] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0
  });

  useEffect(() => {
    fetchData();
  }, [])

  const searchAttendants = async (search) => {
    if (search.length < 2) {
      setAttendants([]);
      return;
    }
    try {
      const response = await api.get(`api/users/search/${search}`);
      setAttendants(response.data.data || []);
    } catch (error) {
      console.error("Erro ao buscar atendentes:", error);
    }
  };

  const searchCompanies = async (search) => {
    if (search.length < 2) {
      setCompanies([]);
      return;
    }
    try {
      const response = await api.get(`/api/affiliatecompany/list?search=${search}`);
      setCompanies(response.data.data || []);

      // last_page comes from response.data.last_page
      // total comes from response.data.total. show on screen?
      // show pagination on screen
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
    }
  };

  useEffect(() => {
    if (isAttendantSelected) return;
    const timer = setTimeout(() => {
      searchAttendants(attendantSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [attendantSearch, isAttendantSelected]);

  useEffect(() => {
    if (isCompanySelected) return;
    const timer = setTimeout(() => {
      searchCompanies(companySearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [companySearch, isCompanySelected]);

  const fetchData = async (page = 1) => {
    setLoading(true);

    await api.post(`api/scheduling-items/filters?page=${page}`, filters)
      .then(response => {
        setProcedures(response.data.data || []);

        setPagination({
          currentPage: response.data.current_page || 1,
          lastPage: response.data.last_page || 1,
          total: response.data.total || 0
        });
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setLoading(false);
      })
  }

  const handleDownloadPDF = async () => {
    const result = await Swal.fire({
      title: 'Deseja baixar relatório como PDF?',
      text: "Confirme para iniciar o download.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, baixar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Gerando relatório...',
        text: 'Aguarde um momento.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      let allProcedures = [];
      for (let page = 1; page <= pagination.lastPage; page++) {
        try {
          const response = await api.post(`api/scheduling-items/filters?page=${page}`, filters);
          allProcedures = [...allProcedures, ...(response.data.data || [])];
        } catch (error) {
          console.error(`Error fetching page ${page}:`, error);
        }
      }

      let reportFilters = {
        attendant: filters.user_id ? allProcedures[0].user.name : null,
        company: filters.executing_company ? allProcedures[0].executing_company : null,
        date_start: filters.date_start,
        date_end: filters.date_end,
      }

      const date = new Date();
      const timestamp = date.getDate() + '-' + date.getMonth() + '-' + date.getFullYear() + '_' + date.getHours() + ':' + date.getMinutes() + ':' + date.getSeconds();
      const blob = await pdf(<ReportPDFAttendants procedures={allProcedures} filters={reportFilters} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Relatorio-Achemed-Atendente-${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Fecha o alerta de loading
      Swal.close();
    }
  }

  const TableComponent = () => (
    <Table striped bordered hover responsive>
      <thead className="table-dark position-sticky">
        <tr>
          <th>Paciente</th>
          <th>Procedimento</th>
          <th>Empresa Realizante</th>
          <th>Data de Realização</th>
          <th className="text-end">Valor de Venda</th>
        </tr>
      </thead>

      <tbody
        style={{
          maxHeight: "40vh",
          overflowY: "auto",
          overflowX: "auto",
        }}
      >
        {procedures.map((procedure, index) => (
          <tr key={index}>
            <td>{procedure.customer_name || '-'}</td>
            <td>{procedure.procedure?.name || '-'}</td>
            <td>
              <Badge bg="primary" className="text-wrap py-1 px-2">
                {procedure.executing_company || '-'}
              </Badge>
            </td>
            <td>
              {formatDate(procedure.date_scheduling)}
            </td>
            <td className="text-end">
              {procedure.price
                ? `R$ ${parseFloat(procedure.price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : 'R$ 0,00'}
            </td>
          </tr>
        ))}
      </tbody>

      <tfoot>
        <tr>
          <th colSpan={4} className="text-end">Total:</th>
          <th className="text-end">
            R$ {procedures.reduce((sum, proc) => sum + (parseFloat(proc.price) || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </th>
        </tr>
      </tfoot>
    </Table>
    // </div>
  );

  const screenTela =
    <div>
      <div
        className="container-fluid py-3"
        style={{
          paddingBottom: "120px",
          borderRadius: "10px",
          height: "calc(100vh - 110px)",
          overflowY: "auto",
        }}>

        <div className="mb-2">
          <h3 className="text-dark mb-1 d-flex align-items-center">
            {/* <DollarSign size={22} className="me-2 text-primary" /> */}
            Relatório de Atendentes
          </h3>
          <p className="text-muted mb-0 very-small">
            Selecione os procedimentos
          </p>
        </div>

        <Card className="shadow-sm border-0 mb-3">
          <Card.Header className="bg-light border-0 py-2">
            <h6 className="mb-0 d-flex align-items-center small">
              <Filter size={16} className="me-2 text-primary" />
              Filtros de Busca
            </h6>
          </Card.Header>

          <Card.Body className="p-2">
            <Row className="g-2">
              <Col xs={12} sm={6} md={3}>
                <Form.Label className="fw-semibold small d-flex align-items-center">
                  <Headphones size={14} className="me-1" />
                  Atendente
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar atendente..."
                  value={attendantSearch}
                  onChange={(e) => {
                    setAttendantSearch(e.target.value);
                    setIsAttendantSelected(false);
                    setFilters({ ...filters, user_id: null });
                  }}
                  size="sm"
                />
                {attendants.length > 0 && !isAttendantSelected && (
                  <div className="position-absolute bg-white border rounded shadow-sm mt-1 w-25" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                    {attendants.map((att) => (
                      <div
                        key={att.id}
                        className="p-2 cursor-pointer hover:bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setFilters({ ...filters, user_id: att.id });
                          setAttendantSearch("");
                          setIsAttendantSelected(true);
                          setSelectedAttendant(att);
                          setAttendants([]);
                        }}
                      >
                        {att.name || att.username}
                      </div>
                    ))}
                  </div>
                )}

                {selectedAttendant && (
                  <Card className="mt-2 border-primary">
                    <Card.Body className="p-2 d-flex justify-content-between align-items-center">
                      <span className="small">{selectedAttendant.name || selectedAttendant.username}</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={() => {
                          setSelectedAttendant(null);
                          setAttendantSearch("");
                          setIsAttendantSelected(false);
                          setFilters({ ...filters, user_id: null });
                        }}
                      >
                        <X size={16} />
                      </Button>
                    </Card.Body>
                  </Card>
                )}
              </Col>

              <Col xs={12} sm={6} md={3}>
                <Form.Label className="fw-semibold small d-flex align-items-center">
                  <Building size={14} className="me-1" />
                  Empresa Realizante
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Buscar empresa..."
                  value={companySearch}
                  onChange={(e) => {
                    setCompanySearch(e.target.value);
                    setIsCompanySelected(false);
                    setFilters({ ...filters, executing_company: null });
                  }}
                  size="sm"
                />
                {companies.length > 0 && !isCompanySelected && (
                  <div className="position-absolute bg-white border rounded shadow-sm mt-1 w-25" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                    {companies.map((comp) => (
                      <div
                        key={comp.id}
                        className="p-2 cursor-pointer hover:bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setFilters({ ...filters, executing_company: comp.name });
                          setCompanySearch("");
                          setIsCompanySelected(true);
                          setSelectedCompany(comp);
                          setCompanies([]);
                        }}
                      >
                        {comp.name || comp.fantasy_name}
                      </div>
                    ))}
                  </div>
                )}

                {selectedCompany && (
                  <Card className="mt-2 border-primary">
                    <Card.Body className="p-2 d-flex justify-content-between align-items-center">
                      <span className="small">{selectedCompany.name || selectedCompany.fantasy_name}</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        onClick={() => {
                          setSelectedCompany(null);
                          setCompanySearch("");
                          setIsCompanySelected(false);
                          setFilters({ ...filters, executing_company: null });
                        }}
                      >
                        <X size={16} />
                      </Button>
                    </Card.Body>
                  </Card>
                )}
              </Col>


              <Col xs={6} sm={3} md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold small d-flex align-items-center">
                    <Calendar size={14} className="me-1" />
                    Data Inicial
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.date_start}
                    onChange={(e) => {
                      setFilters({ ...filters, date_start: e.target.value });
                    }}
                    size="sm"
                  />
                </Form.Group>
              </Col>

              <Col xs={6} sm={3} md={2}>
                <Form.Group>
                  <Form.Label className="fw-semibold small d-flex align-items-center">
                    <Calendar size={14} className="me-1" />
                    Data Final
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.date_end}
                    onChange={(e) => {
                      setFilters({ ...filters, date_end: e.target.value });
                    }}
                    size="sm"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} sm={12} md={2} className="align-self-center">
                <Button
                  variant="primary"
                  size="sm"
                  className="w-100 d-flex align-items-center justify-content-center mb-2"
                  onClick={() => fetchData(1)}
                  disabled={loading}>
                  {loading ? (
                    <Loader2 size={14} className="me-1 spin" />
                  ) : (
                    <Search size={14} className="me-1" />
                  )}
                  {loading ? "Buscando..." : "Buscar"}
                </Button>
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5 small">
                <Loader2 size={32} className="spin" />
                <p className="text-muted mt-2">Carregando procedimentos...</p>
              </div>
            ) : procedures.length > 0 ? (
              <Card
                className="shadow-sm border-0 mb-3"
                style={{
                  overflowY: "auto",
                  overflowX: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.375rem",
                }}>
                <Card.Header className="bg-light border-0 py-2 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center small my-3">
                  <div className="mb-1 mb-md-0">
                    <h6 className="mb-0">Procedimentos Encontrados: {pagination.total}</h6>
                  </div>
                </Card.Header>

                <Card.Body className="p-0">
                  <TableComponent />
                </Card.Body>
              </Card>
            ) : (
              <Card className="shadow-sm border-0 mb-3">
                <Card.Body className="text-center py-5 small">
                  <p className="text-muted mb-0">
                    Nenhum procedimento encontrado para os filtros selecionados.
                  </p>
                </Card.Body>
              </Card>
            )}

            {procedures.length > 0 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="small text-muted">
                  Total: {pagination.total} procedimentos
                </div>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={pagination.currentPage === 1}
                    onClick={() => fetchData(pagination.currentPage - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="small align-self-center">
                    Página {pagination.currentPage} de {pagination.lastPage}
                  </span>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={pagination.currentPage === pagination.lastPage}
                    onClick={() => fetchData(pagination.currentPage + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </Card.Body>

          <Card.Footer>
            <div className="text-end">
              <Button onClick={handleDownloadPDF}>
                <FileText size={16} className="me-2" />
                Salvar como PDF
              </Button>
            </div>
          </Card.Footer>
        </Card>


      </div>
    </div>;

  return <Menu screen={screenTela} />;
}